import type {
	DiscriminatorSpec,
	DiscriminatorSpecFields,
	TaxonomyClass,
	TaxonomyClassFields,
	TaxonomyDraft,
	TaxonomyDraftIssue,
	VerificationTableVersions,
} from "@sitescene/types";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "@/api/client";
import { ENDPOINTS } from "@/api/endpoints";
import { queryKeys } from "@/api/query-keys";

export interface SeedExportFiles {
	discriminators: string;
	taxonomy: string;
}

/** Wire shape of `GET /shadow/plausibility/taxonomy/workspace`. */
export interface TaxonomyWorkspace {
	classes: TaxonomyClass[];
	draft: null | TaxonomyDraft;
	issues: TaxonomyDraftIssue[];
	seeded: boolean;
	/** In-code seed specs: the fallback for a class with no spec row. */
	seedSpecs: { classId: string; fields: DiscriminatorSpecFields }[];
	specs: DiscriminatorSpec[];
	versions: Partial<VerificationTableVersions>;
}

/** Every class and global spec, plus the open draft. Admin DB, not org-scoped. */
export const useTaxonomyWorkspace = () =>
	useQuery({
		queryFn: async () =>
			(
				await apiClient.get<TaxonomyWorkspace>(
					ENDPOINTS.shadow.plausibility.taxonomyWorkspace,
				)
			).data,
		queryKey: queryKeys.plausibility.taxonomyWorkspace,
	});

/**
 * Draft edits and publishing. Every mutation refreshes the workspace; a
 * publish or discard also refreshes the plain taxonomy list the priors and
 * verdict pickers read.
 */
export const useTaxonomyEditor = () => {
	const queryClient = useQueryClient();
	const refresh = () =>
		queryClient.invalidateQueries({
			queryKey: queryKeys.plausibility.taxonomyWorkspace,
		});
	const refreshAll = () =>
		Promise.all([
			refresh(),
			queryClient.invalidateQueries({
				queryKey: queryKeys.plausibility.taxonomy,
			}),
		]);

	const saveClass = useMutation({
		mutationFn: async (input: {
			classId: string;
			create: boolean;
			fields: TaxonomyClassFields;
		}) =>
			(
				await apiClient.put<TaxonomyDraft>(
					ENDPOINTS.shadow.plausibility.taxonomyClass(input.classId),
					{ create: input.create, fields: input.fields },
				)
			).data,
		onSuccess: refresh,
	});

	const revertClass = useMutation({
		mutationFn: async (classId: string) =>
			(
				await apiClient.delete<TaxonomyDraft>(
					ENDPOINTS.shadow.plausibility.taxonomyClass(classId),
				)
			).data,
		onSuccess: refresh,
	});

	const saveSpec = useMutation({
		mutationFn: async (input: {
			classId: string;
			fields: DiscriminatorSpecFields;
		}) =>
			(
				await apiClient.put<TaxonomyDraft>(
					ENDPOINTS.shadow.plausibility.taxonomySpec(input.classId),
					input.fields,
				)
			).data,
		onSuccess: refresh,
	});

	const revertSpec = useMutation({
		mutationFn: async (classId: string) =>
			(
				await apiClient.delete<TaxonomyDraft>(
					ENDPOINTS.shadow.plausibility.taxonomySpec(classId),
				)
			).data,
		onSuccess: refresh,
	});

	const publish = useMutation({
		mutationFn: async () =>
			(
				await apiClient.post<{
					classes: number;
					specs: number;
					versions?: Partial<VerificationTableVersions>;
				}>(ENDPOINTS.shadow.plausibility.taxonomyPublish)
			).data,
		onSettled: refreshAll,
	});

	const discard = useMutation({
		mutationFn: async () =>
			(await apiClient.delete(ENDPOINTS.shadow.plausibility.taxonomyDraft))
				.data,
		onSuccess: refresh,
	});

	const exportSeeds = useMutation({
		mutationFn: async () => {
			const files = (
				await apiClient.get<SeedExportFiles>(
					ENDPOINTS.shadow.plausibility.taxonomyExport,
				)
			).data;
			downloadText("taxonomy.seed.ts", files.taxonomy);
			downloadText("discriminators.seed.ts", files.discriminators);
			return files;
		},
	});

	return {
		discard,
		exportSeeds,
		publish,
		revertClass,
		revertSpec,
		saveClass,
		saveSpec,
	};
};

function downloadText(filename: string, text: string) {
	const url = URL.createObjectURL(
		new Blob([text], { type: "text/typescript" }),
	);
	const link = document.createElement("a");
	link.href = url;
	link.download = filename;
	link.click();
	URL.revokeObjectURL(url);
}
